import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminCreateCustomerDto,
  AdminCreateCustomerResult,
  AdminCustomersFacade,
  AdminCustomerUpdateDto,
} from './admin-customers.facade';
import { AdminCustomerListItem } from '@models/resources/admin-customer.model';
import { AdminTransactionListItemV4 } from '@models/resources/admin-transaction.model';
import { DEMO_ADMIN_CUSTOMERS } from '@data/fixtures/admin-customers.fixtures';
import {
  DEMO_TRANSACTIONS_V4,
  TX_CUSTOMER_ID_BY_TX_ID,
} from '@data/fixtures/transactions-v4.fixtures';
import { dummyError, dummyResponse } from '@data/dummy-response.util';
import { resolveCustomerStub } from '@data/dummy-utils';

/**
 * In-Memory-Variante. Der Store ist eine mutable Kopie der Fixtures,
 * damit `update()` für Folge-`list()`-Calls sichtbar bleibt (Demo-UX:
 * gespeicherter Name erscheint sofort in der Liste).
 */
@Injectable()
export class DummyAdminCustomersFacade implements AdminCustomersFacade {
  private readonly store: AdminCustomerListItem[] = DEMO_ADMIN_CUSTOMERS.map(
    (c) => ({ ...c }),
  );

  list(): Observable<AdminCustomerListItem[]> {
    return dummyResponse('admin.customers', this.store.map((c) => ({ ...c })));
  }

  get(id: number): Observable<AdminCustomerListItem> {
    const match = this.store.find((c) => c.id === id);
    if (!match) {
      return dummyError('admin.customers');
    }
    return dummyResponse('admin.customers', { ...match });
  }

  update(
    id: number,
    dto: AdminCustomerUpdateDto,
  ): Observable<AdminCustomerListItem> {
    const index = this.store.findIndex((c) => c.id === id);
    if (index === -1) {
      return dummyError('admin.customers');
    }
    const current = this.store[index];
    const emailChanged = current.email !== dto.email;
    const updated: AdminCustomerListItem = {
      ...current,
      first_name: dto.first_name,
      last_name: dto.last_name,
      email: dto.email,
      // Bei E-Mail-Change-Re-Activation (Sprint 6 Polish S6-13): User
      // wird automatisch auf inactive gesetzt, Aktivierungs-Status pending.
      is_active: emailChanged ? false : dto.is_active,
      activation_status: emailChanged ? 'pending' : current.activation_status,
    };
    this.store[index] = updated;
    return dummyResponse('admin.customers', { ...updated });
  }

  listTransactions(customerId: number): Observable<AdminTransactionListItemV4[]> {
    const forCustomer: AdminTransactionListItemV4[] = DEMO_TRANSACTIONS_V4
      .filter((t) => TX_CUSTOMER_ID_BY_TX_ID[t.id] === customerId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map(
        (t) =>
          ({
            ...t,
            customer_id: customerId,
            customer: resolveCustomerStub(customerId),
          }) as AdminTransactionListItemV4,
      );
    return dummyResponse('admin.customers', forCustomer);
  }

  resendActivation(customerId: number): Observable<void> {
    const index = this.store.findIndex((x) => x.id === customerId);
    if (index === -1) {
      return dummyError('admin.customers');
    }
    const current = this.store[index];
    if (current.activation_status === 'active') {
      return dummyError('admin.customers');
    }
    // Sprint 6 Review-Fix M-2: Spread statt direkte Property-Mutation,
    // damit `list()` keine geteilten Referenzen rausgibt.
    this.store[index] = { ...current, activation_status: 'pending' };
    return dummyResponse('admin.customers', undefined);
  }

  create(dto: AdminCreateCustomerDto): Observable<AdminCreateCustomerResult> {
    // Eindeutige neue ID — höchste vorhandene + 1.
    const newId = this.store.reduce((max, c) => Math.max(max, c.id), 0) + 1;
    const stub: AdminCustomerListItem = {
      id: newId,
      first_name: dto.first_name,
      last_name: dto.last_name,
      email: dto.email,
      depot_number: `GS-NEW-${String(newId).padStart(5, '0')}`,
      portfolio_value_net_eur: 0,
      customer_since: new Date().toISOString().slice(0, 10),
      is_active: false,
      activation_status: 'pending',
      phone: dto.phone ?? null,
      company: dto.company ?? null,
      address_street: dto.address_street ?? null,
      address_zip: dto.address_zip ?? null,
      address_city: dto.address_city ?? null,
      address_country: dto.address_country ?? null,
      iban: dto.iban ?? null,
      bic: dto.bic ?? null,
      notes: dto.notes ?? null,
    };
    this.store.push(stub);
    return dummyResponse('admin.customers', { user_id: newId, customer_id: newId });
  }
}
